import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import clamp from 'lodash/clamp';
import sum from 'lodash/sum';

interface SplitViewState {
    sizes: number[];
    draggingSizes?: number[];
}

export function getInitialState(sizes: number | number[]): SplitViewState {
    if (typeof sizes === 'number') {
        return {
            sizes: Array.from({ length: sizes }, () => 1 / sizes),
        };
    }
    const total = sum(sizes);
    return {
        sizes: sizes.map((s) => s / total),
    };
}

const splitViewSlice = createSlice({
    name: 'splitView',
    initialState: {} as SplitViewState,
    reducers: {
        reinit(
            state,
            action: PayloadAction<{
                sizes: number | number[];
            }>
        ) {
            const { sizes } = action.payload;

            state.draggingSizes = undefined;
            state.sizes = getInitialState(sizes).sizes;
        },
        resize(
            state,
            action: PayloadAction<{
                movement: number;
                index: number;
                size?: number;
                minSizes?: number[];
            }>
        ) {
            const { movement, index, minSizes, size } = action.payload;

            if (typeof size === 'undefined') {
                return;
            }

            let offset = movement / size;

            state.draggingSizes = [...state.sizes];

            if (minSizes) {
                const minGrowSizes = minSizes.map((s) => s / size);
                const maxOffset = state.draggingSizes[index] - minGrowSizes[index];
                const minOffset = minGrowSizes[index - 1] - state.draggingSizes[index - 1];
                offset = minOffset > maxOffset ? 0 : clamp(offset, minOffset, maxOffset);
            }

            state.draggingSizes[index] -= offset;
            state.draggingSizes[index - 1] += offset;
        },
        resizeEnd(
            state,
            action: PayloadAction<{
                index: number;
                paneSize: number;
                size?: number;
            }>
        ) {
            const { index, size, paneSize } = action.payload;

            if (typeof size === 'undefined') {
                return;
            }

            const targetSize = paneSize / size;
            const offset = state.sizes[index] - targetSize;

            state.sizes[index] -= offset;
            state.sizes[index - 1] += offset;

            state.draggingSizes = undefined;
        },
    },
});

export const { reducer, actions } = splitViewSlice;
